import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import config from '@this/impl/config';
import { getData, patchData, patchForm, postData, postForm } from './base';
import Structure from './models/structure';
import Document from './models/document';
import Revision from './models/revision';
import Category from './models/category';
import Section from './models/section';
import DocumentType from './models/documentType';
import { IEntityQuery, useEntityQuery } from './entityQuery';
import { ApiOptions } from './options';
import ProcessorRule from './models/processorRule';
import DocumentHistory from './models/documentHistory';
import Configuration from './models/configuration';
import DocumentAction from './models/documentAction';

const useSections = (id?: string) =>
	useEntityQuery<Section>({ name: 'sections', path: '/contents/sections', id });

const useConfigurations = (options: ApiOptions = { enabled: true, refetchOnMount: true }) => {
	return useEntityQuery<Configuration>({
		name: 'configurations',
		path: '/contents/configurations',
		...options,
	});
};

const useCategories = (options: ApiOptions = { enabled: true, refetchOnMount: true }) => {
	return useEntityQuery<Category>({
		name: 'categories',
		path: '/contents/category',
		...options,
	});
};

const useStructures = (options: ApiOptions = { enabled: false, refetchOnMount: true }) =>
	useEntityQuery<Structure>({
		name: 'structure',
		path: '/contents/structure',
		...options,
	});

const useProcessorRules = (id?: string) =>
	useEntityQuery<ProcessorRule>({
		name: 'processorRules',
		path: '/contents/processor/rules',
		id,
		enabled: true,
	});

const useFavoriteDocuments = (options?: ApiOptions) => {
	return useQuery<Document[], Error>(
		['favoriteDocuments'],
		getData('/contents/documents/favorites', { onlyPublic: false }),
	);
};

const useDocuments = (options?: ApiOptions) => {
	const entityQuery = useEntityQuery<Document>({
		name: 'documents',
		path: '/contents/documents',
		tree: false,
		...options,
	});

	const query = {
		...entityQuery,
		upload: async (entity: Document) => {
			if (entity.id) {
				const { id } = entity;
				delete entity.id;
				const result = await patchForm(`/contents/documents/${id}/upload`, entity);
				return result.data;
			}
			const result = await postForm(`/contents/documents/upload`, entity);
			return result.data;
		},
		invalidate: async (id?: string) => {
			if (id) await getData(`/contents/documents/${id}/invalidate`);
			entityQuery.invalidate();
		},
	};

	return query;
};

const useDocumentActions = (id?: string) => {
	return useQuery<DocumentAction[]>(
		['document-actiions', id],
		getData(`/contents/documents/${id}/actions`),
		{
			enabled: !!id,
		},
	);
};

const useDocumentContent = (id?: string) => {
	return useQuery(['document-content', id], getData(`/contents/documents/${id}/view`), {
		enabled: !!id,
	});
};

const useDocumentTypes = () =>
	useQuery<DocumentType[], Error>(['documentTypes'], getData(`/contents/documents/types`));

const useDocumentHistory = (id: String) => {
	return useQuery<DocumentHistory[], Error>(
		['history', id],
		getData(`/contents/documents/${id}/history`),
		{
			enabled: id !== undefined,
		},
	);
};

const useDocumentRevisions = (
	documentId: string | undefined,
	revisionId: string | undefined,
	options?: ApiOptions,
) => {
	const path = `/contents/documents/${documentId}/revisions`;
	const name = `revisions${documentId}`;
	const enabled = true;
	return {
		useQuery: () => {
			const queryClient = useQueryClient();
			return {
				tree: useQuery<Revision[], Error>([name], getData(`${path}`), {
					refetchOnWindowFocus: false,
					refetchOnMount: false,
					enabled: false,
				}),
				all: useQuery<Revision[], Error>([name], getData(`${path}`), {
					refetchOnWindowFocus: false,
					refetchOnMount: true,
					enabled,
				}),
				query: useQuery<Revision[], Error>([name], getData(`${path}`), {
					refetchOnWindowFocus: false,
					refetchOnMount: false,
					enabled: false,
				}),
				one: useQuery<Revision, Error>(
					[name, revisionId],
					getData(`${path}/${revisionId}`),
					{
						refetchOnWindowFocus: true,
						enabled: revisionId !== undefined,
						refetchOnMount: true,
					},
				),
				save: async (entity: any) => {
					const result = await patchForm(`${path}/${entity.number}`, {
						label: entity.label,
						note: entity.note,
						description: entity.description,
						startDate: `${entity.startDate}`,
						categories: entity.categories,
						file: entity.file,
					});
					return result.data;
				},
				invalidate: () => {
					queryClient.invalidateQueries([name]);
					if (revisionId) {
						queryClient.invalidateQueries([name, revisionId]);
					}
				},
			} as IEntityQuery<Revision>;
		},
		publish: async (revision: String) => {
			const result = await postData(
				`/contents/documents/${documentId}/publish/${revision}`,
				{},
			);
			if (result.status >= 299) {
				// eslint-disable-next-line no-console
				console.error('Error trying to publish.');
			}
		},
		unpublish: async (revision: String) => {
			const result = await postData(
				`/contents/documents/${documentId}/unpublish/${revision}`,
				{},
			);
			if (result.status >= 299) {
				// eslint-disable-next-line no-console
				console.error('Error trying to unpublish.');
			}
		},
		forceKeepForever: async () => {
			const result = await postData(
				`/contents/documents/maintenance/keep-forever-revisions/${documentId}`,
				{},
			);
			if (result.status >= 299) {
				// eslint-disable-next-line no-console
				console.error('Error trying to force keep forever.');
			}
			return result;
		},
	};
};

/**
 * Soporte: diagnóstico de visibilidad de una carpeta (por nombre o id).
 */
export const diagnoseFolder = async (query: string) => {
	const response = await axios.get(`${config.apiHost}/contents/support/folders/diagnose`, {
		params: { query },
	});
	return response.data;
};

const useRelatedDocuments = (id: String | undefined) =>
	useQuery<Document[], Error>(
		['relatedDocuments', id],
		getData(`/contents/documents/${id}/relateds`),
		{
			enabled: id !== undefined,
			refetchOnWindowFocus: false,
			refetchOnMount: false,
		},
	);

/**
 * Remove the relationship between two documents. The backend cuts the link in
 * BOTH directions (A->B and B->A): a mutual link used to reappear as linkedToMe
 * after unlinking, with no way to remove it from this screen.
 */
const unlinkRelatedDocument = async (documentId: string, relatedId: string) => {
	const response = await axios.delete(
		`${config.apiHost}/contents/documents/${documentId}/relateds/${relatedId}`,
	);
	return response.data;
};

/**
 * Upload multiple auditory documents for a revision
 */
const uploadAuditoryDocuments = async (documentId: string, revisionId: string, files: File[]) => {
	// Validate max files (backend allows up to 10)
	if (files.length > 10) {
		throw new Error('Maximum 10 files allowed');
	}

	const formData = new FormData();
	// Append each file with the field name "files" - this creates an array on the backend
	files.forEach((file, index) => {
		formData.append('files', file);
	});

	// Use axios directly since FormData can't be spread by postForm
	const response = await axios.post(
		`${config.apiHost}/contents/documents/${documentId}/revisions/${revisionId}/auditory-documents`,
		formData,
		{
			headers: {
				'Content-Type': 'multipart/form-data',
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);

	return response.data;
};

/**
 * Get list of auditory documents for a revision
 */
const getAuditoryDocuments = async (documentId: string, revisionId: string | number) => {
	// Ensure revisionId is string
	const revisionIdStr = String(revisionId);
	const endpoint = `/contents/documents/${documentId}/revisions/${revisionIdStr}/auditory-documents`;

	try {
		const result = await getData(endpoint)();
		return result;
	} catch (err) {
		// eslint-disable-next-line no-console
		console.error('getAuditoryDocuments - Error:', err);
		throw err;
	}
};

/**
 * Download all auditory documents as ZIP
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const downloadAuditoryDocumentsZip = async (documentId: string, revisionId: string) => {
	// For blob downloads, we'll handle this in the component directly with axios
	// since the existing getData doesn't support blob responses
	const endpoint = `/contents/documents/${documentId}/revisions/${revisionId}/auditory-documents/download`;
	return { endpoint }; // Return endpoint for component to handle
};

/**
 * Delete specific auditory document
 */
const deleteAuditoryDocument = async (
	documentId: string,
	revisionId: string,
	auditoryDocId: string,
) => {
	// Use direct DELETE request to match backend endpoint
	const response = await axios.delete(
		`${config.apiHost}/contents/documents/${documentId}/revisions/${revisionId}/auditory-documents/${auditoryDocId}`,
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

// Add React Query hooks
const useAuditoryDocuments = (documentId: string, revisionId: string | number) => {
	const enabled = !!documentId && !!revisionId;

	return useQuery({
		queryKey: ['auditory-documents', documentId, revisionId],
		queryFn: () => {
			return getAuditoryDocuments(documentId, revisionId);
		},
		enabled,
		refetchOnWindowFocus: false,
		refetchOnMount: true,
		staleTime: 0, // Always consider data stale to ensure fresh fetches
	});
};

const useUploadAuditoryDocuments = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			documentId,
			revisionId,
			files,
		}: {
			documentId: string;
			revisionId: string;
			files: File[];
		}) => uploadAuditoryDocuments(documentId, revisionId, files),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries([
				'auditory-documents',
				variables.documentId,
				variables.revisionId,
			]);
		},
	});
};

const useDeleteAuditoryDocument = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			documentId,
			revisionId,
			auditoryDocId,
		}: {
			documentId: string;
			revisionId: string;
			auditoryDocId: string;
		}) => deleteAuditoryDocument(documentId, revisionId, auditoryDocId),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries([
				'auditory-documents',
				variables.documentId,
				variables.revisionId,
			]);
		},
	});
};

// Add favorite toggle API method
export const toggleDocumentFavorite = async (
	documentId: string,
	favorite: boolean,
): Promise<void> => {
	await patchData(`/contents/documents/${documentId}/favorite`, { favorite });
};

export const useToggleDocumentFavorite = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ documentId, favorite }: { documentId: string; favorite: boolean }) =>
			toggleDocumentFavorite(documentId, favorite),
		onSuccess: (_, variables) => {
			// Invalidate all document-related queries to ensure favorite status is updated everywhere
			queryClient.invalidateQueries(['documents']); // All documents list
			queryClient.invalidateQueries(['documents', variables.documentId]); // Specific document
			queryClient.invalidateQueries(['documents', 'favorites']); // Favorites list (public API)
			queryClient.invalidateQueries(['documents', 'recent-viewed']); // Recent viewed list
			queryClient.invalidateQueries(['documents', 'most-viewed']); // Most viewed list
			queryClient.invalidateQueries(['favoriteDocuments']); // Favorites list (contents API)
			queryClient.invalidateQueries(['relatedDocuments']); // Related documents
		},
	});
};

// ============================================
// Technical Support API Types and Hooks
// ============================================

// Types for missing revisions report
export interface MissingRevision {
	revisionNumber: string;
	date: string;
	olderThan30Days: boolean;
	label: string;
	lastModifyingUser: string;
}

export interface MissingRevisionsDocument {
	documentId: string;
	documentName: string;
	contentType: string;
	missingRevisions: MissingRevision[];
}

export interface MissingRevisionsReport {
	totalDocuments: number;
	documentsWithMissingRevisions: number;
	totalMissingRevisions: number;
	report: MissingRevisionsDocument[];
}

// Types for keepForever operation
export interface KeepForeverFailure {
	documentId: string;
	revisionNumber: string;
	error: string;
}

export interface KeepForeverResult {
	totalDocuments: number;
	totalRevisions: number;
	successCount: number;
	failureCount: number;
	skippedCount: number;
	failures: KeepForeverFailure[];
	documentLogs: KeepForeverDocumentLog[];
}

// Types for sync operation
export interface SyncResult {
	status: string;
}

// Types for background jobs
export interface JobProgress {
	percent: number;
	processed: number;
	total: number;
	currentDocument?: string;
	successCount?: number;
	failureCount?: number;
}

export interface JobStartResponse {
	jobId: string;
	status: string;
	message: string;
	timestamp: string;
}

export type JobStatus = 'waiting' | 'active' | 'completed' | 'failed' | 'delayed' | 'paused';

export interface JobStatusResponse<T = unknown> {
	jobId: string;
	name: string;
	status: JobStatus;
	progress: JobProgress;
	result: T | null;
	error: string | null;
	createdAt: string | null;
	processedAt: string | null;
	completedAt: string | null;
}

// Types for paginated report data
export interface ReportPagination {
	page: number;
	pageSize: number;
	totalItems: number;
	totalPages: number;
}

export interface ReportSummary {
	totalDocuments: number;
	documentsWithMissingRevisions: number;
	totalMissingRevisions: number;
}

export interface PaginatedReportResponse {
	data: MissingRevisionsDocument[];
	pagination: ReportPagination;
	summary: ReportSummary;
}

export interface ReportSearchParams {
	query?: string;
	contentType?: string;
	page?: number;
	pageSize?: number;
}

// =========================================
// KeepForever Report Types
// =========================================

export interface KeepForeverRevisionLog {
	revisionNumber: string;
	status: 'success' | 'skipped' | 'failed';
	wasAlreadySet: boolean;
	error?: string;
}

export interface KeepForeverDocumentLog {
	id: string; // MongoDB _id for linking to document edition
	documentId: string; // Google Drive externalId
	documentName: string;
	contentType: string;
	revisionsProcessed: number;
	revisionsSuccessful: number;
	revisionsSkipped: number;
	revisionsFailed: number;
	revisions: KeepForeverRevisionLog[];
}

export interface KeepForeverReportSummary {
	totalDocuments: number;
	totalRevisions: number;
	successCount: number;
	failureCount: number;
	skippedCount: number;
}

export interface PaginatedKeepForeverReportResponse {
	data: KeepForeverDocumentLog[];
	pagination: ReportPagination;
	summary: KeepForeverReportSummary;
}

export interface KeepForeverReportSearchParams extends ReportSearchParams {
	status?: 'success' | 'failed' | 'skipped' | 'mixed';
}

export interface LatestKeepForeverReportResponse {
	available: boolean;
	jobId?: string;
	createdAt?: string;
	expiresAt?: string;
	summary?: KeepForeverReportSummary;
	message?: string;
}

export interface FullKeepForeverReport {
	jobId: string;
	createdAt: string;
	expiresAt: string;
	summary: KeepForeverReportSummary;
	documentLogs: KeepForeverDocumentLog[];
	failures: KeepForeverFailure[];
}

/**
 * Generate missing revisions report for non-Google documents (legacy synchronous endpoint)
 */
const generateMissingRevisionsReport = async (): Promise<MissingRevisionsReport> => {
	const response = await axios.get(
		`${config.apiHost}/contents/documents/reports/missing-revisions`,
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

/**
 * Set keepForever flag on all revisions for non-Google documents (legacy synchronous endpoint)
 */
const setKeepForeverOnAllRevisions = async (): Promise<KeepForeverResult> => {
	const response = await axios.post(
		`${config.apiHost}/contents/documents/maintenance/keep-forever-revisions`,
		{},
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

// =========================================
// Background Job API Functions
// =========================================

/**
 * Start a background job to generate missing revisions report
 */
const startMissingRevisionsReportJob = async (): Promise<JobStartResponse> => {
	const response = await axios.post(
		`${config.apiHost}/contents/support/jobs/missing-revisions-report`,
		{},
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

/**
 * Start a background job to set keepForever flag on all revisions
 */
const startKeepForeverJob = async (): Promise<JobStartResponse> => {
	const response = await axios.post(
		`${config.apiHost}/contents/support/jobs/keep-forever-revisions`,
		{},
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

/**
 * Get the status of a background job
 */
const getJobStatus = async <T = unknown>(jobId: string): Promise<JobStatusResponse<T>> => {
	const response = await axios.get(`${config.apiHost}/contents/support/jobs/${jobId}`, {
		headers: {
			Authorization: axios.defaults.headers.common.Authorization,
		},
	});
	return response.data;
};

/**
 * Get all jobs from the queue
 */
export interface AllJobsResponse {
	waiting: Array<{
		jobId: string;
		name: string;
		progress: JobProgress;
		createdAt: string | null;
		processedAt: string | null;
		completedAt: string | null;
	}>;
	active: Array<{
		jobId: string;
		name: string;
		progress: JobProgress;
		createdAt: string | null;
		processedAt: string | null;
		completedAt: string | null;
	}>;
	completed: Array<{
		jobId: string;
		name: string;
		progress: JobProgress;
		createdAt: string | null;
		processedAt: string | null;
		completedAt: string | null;
	}>;
	failed: Array<{
		jobId: string;
		name: string;
		progress: JobProgress;
		createdAt: string | null;
		processedAt: string | null;
		completedAt: string | null;
	}>;
	timestamp: string;
}

const getAllJobs = async (): Promise<AllJobsResponse> => {
	const response = await axios.get(`${config.apiHost}/contents/support/jobs`, {
		headers: {
			Authorization: axios.defaults.headers.common.Authorization,
		},
	});
	return response.data;
};

// =========================================
// Report Data Functions (Pagination, Search, Export)
// =========================================

/**
 * Get paginated report data
 */
const getReportData = async (
	jobId: string,
	page: number = 1,
	pageSize: number = 20,
): Promise<PaginatedReportResponse> => {
	const response = await axios.get(`${config.apiHost}/contents/support/reports/${jobId}/data`, {
		params: { page, pageSize },
		headers: {
			Authorization: axios.defaults.headers.common.Authorization,
		},
	});
	return response.data;
};

/**
 * Search report data
 */
const searchReportData = async (
	jobId: string,
	params: ReportSearchParams,
): Promise<PaginatedReportResponse> => {
	const response = await axios.get(`${config.apiHost}/contents/support/reports/${jobId}/search`, {
		params,
		headers: {
			Authorization: axios.defaults.headers.common.Authorization,
		},
	});
	return response.data;
};

/**
 * Get content types for filtering
 */
const getReportContentTypes = async (jobId: string): Promise<{ contentTypes: string[] }> => {
	const response = await axios.get(
		`${config.apiHost}/contents/support/reports/${jobId}/content-types`,
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

/**
 * Export report as PDF - returns the URL to download
 */
const getReportPdfUrl = (jobId: string): string => {
	return `${config.apiHost}/contents/support/reports/${jobId}/export/pdf`;
};

/**
 * Get the latest available report
 */
export interface LatestReportResponse {
	available: boolean;
	jobId?: string;
	createdAt?: string;
	expiresAt?: string;
	summary?: {
		totalDocuments: number;
		documentsWithMissingRevisions: number;
		totalMissingRevisions: number;
	};
	message?: string;
}

const getLatestReport = async (): Promise<LatestReportResponse> => {
	const response = await axios.get(`${config.apiHost}/contents/support/reports/latest`, {
		headers: {
			Authorization: axios.defaults.headers.common.Authorization,
		},
	});
	return response.data;
};

// =========================================
// KeepForever Report Data Functions
// =========================================

/**
 * Get paginated keepForever report data
 */
const getKeepForeverReportData = async (
	jobId: string,
	page: number = 1,
	pageSize: number = 20,
): Promise<PaginatedKeepForeverReportResponse> => {
	const response = await axios.get(
		`${config.apiHost}/contents/support/keepforever-reports/${jobId}/data`,
		{
			params: { page, pageSize },
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

/**
 * Search keepForever report data
 */
const searchKeepForeverReportData = async (
	jobId: string,
	params: KeepForeverReportSearchParams,
): Promise<PaginatedKeepForeverReportResponse> => {
	const response = await axios.get(
		`${config.apiHost}/contents/support/keepforever-reports/${jobId}/search`,
		{
			params,
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

/**
 * Get the latest available keepForever report
 */
const getLatestKeepForeverReport = async (): Promise<LatestKeepForeverReportResponse> => {
	const response = await axios.get(
		`${config.apiHost}/contents/support/keepforever-reports/latest`,
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

/**
 * Get full keepForever report
 */
const getFullKeepForeverReport = async (jobId: string): Promise<FullKeepForeverReport> => {
	const response = await axios.get(
		`${config.apiHost}/contents/support/keepforever-reports/${jobId}/full`,
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

/**
 * Trigger document sync for all documents
 */
const syncAllDocuments = async (): Promise<SyncResult> => {
	const response = await axios.post(
		`${config.apiHost}/contents/documents/sync`,
		{},
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

/**
 * Trigger document sync for a specific document
 */
const syncDocument = async (documentId: string): Promise<SyncResult> => {
	const response = await axios.post(
		`${config.apiHost}/contents/documents/${documentId}/sync`,
		{},
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

/**
 * Trigger document indexing for a specific document
 */
const indexDocument = async (documentId: string): Promise<void> => {
	await axios.post(
		`${config.apiHost}/contents/documents/${documentId}/index`,
		{},
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
};

/**
 * Invalidate cache for a specific document
 */
const invalidateDocumentCache = async (documentId: string): Promise<void> => {
	await axios.get(`${config.apiHost}/contents/documents/${documentId}/invalidate`, {
		headers: {
			Authorization: axios.defaults.headers.common.Authorization,
		},
	});
};

// React Query hooks for support operations

export const useGenerateMissingRevisionsReport = () => {
	return useMutation({
		mutationFn: generateMissingRevisionsReport,
	});
};

export const useSetKeepForeverOnAllRevisions = () => {
	return useMutation({
		mutationFn: setKeepForeverOnAllRevisions,
	});
};

// Background Job Hooks

export const useStartMissingRevisionsReportJob = () => {
	return useMutation({
		mutationFn: startMissingRevisionsReportJob,
	});
};

export const useStartKeepForeverJob = () => {
	return useMutation({
		mutationFn: startKeepForeverJob,
	});
};

export const useJobStatus = <T = unknown>(
	jobId: string | null,
	options?: { enabled?: boolean; refetchInterval?: number | false },
) => {
	return useQuery<JobStatusResponse<T>, Error>(
		['job-status', jobId],
		() => getJobStatus<T>(jobId!),
		{
			enabled: !!jobId && options?.enabled !== false,
			refetchInterval: options?.refetchInterval ?? 2000, // Poll every 2 seconds by default
			refetchOnWindowFocus: false,
		},
	);
};

export const useAllJobs = (options?: { enabled?: boolean; refetchInterval?: number | false }) => {
	return useQuery<AllJobsResponse, Error>(['all-jobs'], () => getAllJobs(), {
		enabled: options?.enabled !== false,
		refetchInterval: options?.refetchInterval ?? 5000, // Poll every 5 seconds by default
		refetchOnWindowFocus: true,
	});
};

// Report Data Hooks

export const useReportData = (
	jobId: string | null,
	page: number = 1,
	pageSize: number = 20,
	options?: { enabled?: boolean },
) => {
	return useQuery<PaginatedReportResponse, Error>(
		['report-data', jobId, page, pageSize],
		() => getReportData(jobId!, page, pageSize),
		{
			enabled: !!jobId && options?.enabled !== false,
			refetchOnWindowFocus: false,
			staleTime: 30000, // 30 seconds
		},
	);
};

export const useSearchReportData = (
	jobId: string | null,
	params: ReportSearchParams,
	options?: { enabled?: boolean },
) => {
	return useQuery<PaginatedReportResponse, Error>(
		['report-search', jobId, params],
		() => searchReportData(jobId!, params),
		{
			enabled: !!jobId && options?.enabled !== false,
			refetchOnWindowFocus: false,
			staleTime: 30000,
		},
	);
};

export const useReportContentTypes = (jobId: string | null, options?: { enabled?: boolean }) => {
	return useQuery<{ contentTypes: string[] }, Error>(
		['report-content-types', jobId],
		() => getReportContentTypes(jobId!),
		{
			enabled: !!jobId && options?.enabled !== false,
			refetchOnWindowFocus: false,
			staleTime: 60000, // 1 minute
		},
	);
};

export { getReportPdfUrl };

// KeepForever Report Data Hooks

export const useKeepForeverReportData = (
	jobId: string | null,
	page: number = 1,
	pageSize: number = 20,
	options?: { enabled?: boolean },
) => {
	return useQuery<PaginatedKeepForeverReportResponse, Error>(
		['keepforever-report-data', jobId, page, pageSize],
		() => getKeepForeverReportData(jobId!, page, pageSize),
		{
			enabled: !!jobId && options?.enabled !== false,
			refetchOnWindowFocus: false,
			staleTime: 30000, // 30 seconds
		},
	);
};

export const useSearchKeepForeverReportData = (
	jobId: string | null,
	params: KeepForeverReportSearchParams,
	options?: { enabled?: boolean },
) => {
	return useQuery<PaginatedKeepForeverReportResponse, Error>(
		['keepforever-report-search', jobId, params],
		() => searchKeepForeverReportData(jobId!, params),
		{
			enabled: !!jobId && options?.enabled !== false,
			refetchOnWindowFocus: false,
			staleTime: 30000,
		},
	);
};

export const useLatestKeepForeverReport = () => {
	return useQuery<LatestKeepForeverReportResponse, Error>(
		['latest-keepforever-report'],
		() => getLatestKeepForeverReport(),
		{
			refetchOnWindowFocus: true,
			staleTime: 10000, // 10 seconds
		},
	);
};

export const useFullKeepForeverReport = (jobId: string | null, options?: { enabled?: boolean }) => {
	return useQuery<FullKeepForeverReport, Error>(
		['full-keepforever-report', jobId],
		() => getFullKeepForeverReport(jobId!),
		{
			enabled: !!jobId && options?.enabled !== false,
			refetchOnWindowFocus: false,
			staleTime: 60000, // 1 minute
		},
	);
};

export const useLatestReport = () => {
	return useQuery<LatestReportResponse, Error>(['latest-report'], () => getLatestReport(), {
		refetchOnWindowFocus: false,
		staleTime: 30000, // 30 seconds
	});
};

// =========================================
// Documents Inventory Report
// =========================================

export interface DocumentsInventoryReportRow {
	externalId: string;
	ruta: string;
	categorias: string[];
	nombre: string;
	contentType: string;
	published: boolean;
	version?: string;
	publishedAt?: string;
}

export interface DocumentsInventoryReportSummary {
	totalDocuments: number;
	totalCategories: number;
	totalPublished: number;
}

export interface PaginatedDocumentsInventoryResponse {
	data: DocumentsInventoryReportRow[];
	pagination: ReportPagination;
	summary: DocumentsInventoryReportSummary;
}

export interface DocumentsInventorySearchParams {
	query?: string;
	published?: 'true' | 'false';
	category?: string;
	/** Publication date lower bound, YYYY-MM-DD, inclusive */
	publishedFrom?: string;
	/** Publication date upper bound, YYYY-MM-DD, inclusive */
	publishedTo?: string;
	page?: number;
	pageSize?: number;
}

export interface LatestDocumentsInventoryReportResponse {
	available: boolean;
	jobId?: string;
	createdAt?: string;
	expiresAt?: string;
	summary?: DocumentsInventoryReportSummary;
	message?: string;
}

const startDocumentsInventoryReportJob = async (): Promise<JobStartResponse> => {
	const response = await axios.post(
		`${config.apiHost}/contents/support/jobs/documents-inventory-report`,
		{},
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

const searchDocumentsInventoryReport = async (
	jobId: string,
	params: DocumentsInventorySearchParams,
): Promise<PaginatedDocumentsInventoryResponse> => {
	const response = await axios.get(
		`${config.apiHost}/contents/support/documents-inventory/${jobId}/search`,
		{
			params,
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

const getDocumentsInventoryReportCategories = async (
	jobId: string,
): Promise<{ categories: string[] }> => {
	const response = await axios.get(
		`${config.apiHost}/contents/support/documents-inventory/${jobId}/categories`,
		{
			headers: {
				Authorization: axios.defaults.headers.common.Authorization,
			},
		},
	);
	return response.data;
};

const getLatestDocumentsInventoryReport =
	async (): Promise<LatestDocumentsInventoryReportResponse> => {
		const response = await axios.get(
			`${config.apiHost}/contents/support/documents-inventory/latest`,
			{
				headers: {
					Authorization: axios.defaults.headers.common.Authorization,
				},
			},
		);
		return response.data;
	};

const getDocumentsInventoryReportCsvUrl = (jobId: string): string => {
	return `${config.apiHost}/contents/support/documents-inventory/${jobId}/export/csv`;
};

export const useStartDocumentsInventoryReportJob = () => {
	return useMutation({
		mutationFn: startDocumentsInventoryReportJob,
	});
};

export const useSearchDocumentsInventoryReport = (
	jobId: string | null,
	params: DocumentsInventorySearchParams,
	options?: { enabled?: boolean },
) => {
	return useQuery<PaginatedDocumentsInventoryResponse, Error>(
		['documents-inventory-search', jobId, params],
		() => searchDocumentsInventoryReport(jobId!, params),
		{
			enabled: !!jobId && options?.enabled !== false,
			refetchOnWindowFocus: false,
			staleTime: 30000,
		},
	);
};

export const useDocumentsInventoryReportCategories = (
	jobId: string | null,
	options?: { enabled?: boolean },
) => {
	return useQuery<{ categories: string[] }, Error>(
		['documents-inventory-categories', jobId],
		() => getDocumentsInventoryReportCategories(jobId!),
		{
			enabled: !!jobId && options?.enabled !== false,
			refetchOnWindowFocus: false,
			staleTime: 60000,
		},
	);
};

export const useLatestDocumentsInventoryReport = () => {
	return useQuery<LatestDocumentsInventoryReportResponse, Error>(
		['latest-documents-inventory-report'],
		() => getLatestDocumentsInventoryReport(),
		{
			refetchOnWindowFocus: false,
			staleTime: 30000,
		},
	);
};

export { getDocumentsInventoryReportCsvUrl };

// =========================================
// Admin documents listing (paginated, recursive folder filter)
// =========================================

export interface AdminDocumentsSearchParams {
	q?: string;
	/** Comma-separated category ids */
	categories?: string;
	/** Folder id */
	parent?: string;
	/** Include documents of every subfolder of `parent` */
	recursive?: boolean;
	published?: 'true' | 'false';
	page?: number;
	pageSize?: number;
}

export type AdminDocumentRow = Document & {
	/** Path of the document folder relative to the filtered folder ("" = the folder itself) */
	folderPath?: string;
	published?: boolean;
	modificationDate?: string;
};

export interface AdminDocumentsSearchResponse {
	data: AdminDocumentRow[];
	pagination: ReportPagination;
	/** Totals for the same filters without `published` */
	summary: { total: number; published: number; unpublished: number };
	folder?: { id: string; name: string; path: string };
}

export interface FolderDocumentCounts {
	direct: number;
	directPublished: number;
	total: number;
	totalPublished: number;
}

const searchAdminDocuments = async (
	params: AdminDocumentsSearchParams,
): Promise<AdminDocumentsSearchResponse> => {
	const response = await axios.get(`${config.apiHost}/contents/documents/admin-search`, {
		params,
		headers: {
			Authorization: axios.defaults.headers.common.Authorization,
		},
	});
	return response.data;
};

export type AdminDocumentsExportFormat = 'csv' | 'xlsx';

/** URL of the full (unpaginated) export of the admin search, as CSV or as a styled Excel workbook. */
const getAdminDocumentsExportUrl = (
	params: AdminDocumentsSearchParams,
	format: AdminDocumentsExportFormat,
): string => {
	const query = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== '' && key !== 'page' && key !== 'pageSize')
			query.set(key, String(value));
	});
	return `${config.apiHost}/contents/documents/admin-search/export/${format}?${query.toString()}`;
};

export const useAdminDocumentsSearch = (
	params: AdminDocumentsSearchParams,
	options?: { enabled?: boolean },
) => {
	// The key starts with 'documents' so useDocuments().invalidate() refreshes it.
	return useQuery<AdminDocumentsSearchResponse, Error>(
		['documents', 'admin-search', params],
		() => searchAdminDocuments(params),
		{
			enabled: options?.enabled !== false,
			keepPreviousData: true,
			refetchOnWindowFocus: false,
		},
	);
};

export const useStructureDocumentCounts = () => {
	// The key starts with 'structure' so saving a structure refreshes it.
	return useQuery<Record<string, FolderDocumentCounts>, Error>(
		['structure', 'document-counts'],
		getData('/contents/structure/document-counts'),
		{
			refetchOnWindowFocus: false,
			staleTime: 60000,
		},
	);
};

export { getAdminDocumentsExportUrl };

export const useSyncAllDocuments = () => {
	return useMutation({
		mutationFn: syncAllDocuments,
	});
};

export const useSyncDocument = () => {
	return useMutation({
		mutationFn: (documentId: string) => syncDocument(documentId),
	});
};

export const useIndexDocument = () => {
	return useMutation({
		mutationFn: (documentId: string) => indexDocument(documentId),
	});
};

export const useInvalidateDocumentCache = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (documentId: string) => invalidateDocumentCache(documentId),
		onSuccess: () => {
			// Also invalidate local React Query cache
			queryClient.invalidateQueries(['documents']);
		},
	});
};

export default {
	useDocumentTypes,
	useRelatedDocuments,
	unlinkRelatedDocument,
	useDocuments,
	useStructures,
	useSections,
	useDocumentRevisions,
	useCategories,
	useProcessorRules,
	useDocumentHistory,
	useConfigurations,
	useDocumentContent,
	useDocumentActions,
	useAuditoryDocuments,
	useUploadAuditoryDocuments,
	useDeleteAuditoryDocument,
	useToggleDocumentFavorite,
	useFavoriteDocuments,
	// Support tools (legacy synchronous)
	useGenerateMissingRevisionsReport,
	useSetKeepForeverOnAllRevisions,
	useSyncAllDocuments,
	useSyncDocument,
	useIndexDocument,
	useInvalidateDocumentCache,
	// Support tools (background jobs)
	useStartMissingRevisionsReportJob,
	useStartKeepForeverJob,
	useJobStatus,
	useAllJobs,
	// Report data (pagination, search, export)
	useReportData,
	useSearchReportData,
	useReportContentTypes,
	useLatestReport,
	getReportPdfUrl,
	// KeepForever report data
	useKeepForeverReportData,
	useSearchKeepForeverReportData,
	useLatestKeepForeverReport,
	useFullKeepForeverReport,
	// Documents inventory report
	useStartDocumentsInventoryReportJob,
	useSearchDocumentsInventoryReport,
	useDocumentsInventoryReportCategories,
	useLatestDocumentsInventoryReport,
	getDocumentsInventoryReportCsvUrl,
	// Admin documents listing and folder counts
	useAdminDocumentsSearch,
	useStructureDocumentCounts,
	getAdminDocumentsExportUrl,
};
